/// <reference path="ReconciliationEntryState.ts" />

interface InvoiceSectionReconciliationHeaderDelegate
{
    headerDidSelectDocument(header:InvoiceSectionReconciliationHeader, document:Invoice):void;
    headerDidChangeAccounts(header:InvoiceSectionReconciliationHeader, document:Invoice):void;
}

// The head of an invoice in the reconciliation list. Selecting it shows the invoice's journal
// entry in the panel. Its dropdowns set the document's default ledger account, which the lines
// without an account of their own inherit, and the classification of all its lines.
class InvoiceSectionReconciliationHeader extends MUIView
{
    delegate:InvoiceSectionReconciliationHeaderDelegate = null;

    private dateLabel:MUILabel = null;
    private venueLabel:MUILabel = null;
    private docNumberLabel:MUILabel = null;
    private partyLabel:MUILabel = null;
    private accountDropdown:MUIButton = null;
    private classificationDropdown:MUIButton = null;
    private totalLabel:MUILabel = null;

    awakeFromHTML(){
        this.dateLabel = MUIOutlet(this, "date-lbl", "MUILabel");
        this.venueLabel = MUIOutlet(this, "venue-lbl", "MUILabel");
        this.docNumberLabel = MUIOutlet(this, "doc-number-lbl", "MUILabel");
        this.partyLabel = MUIOutlet(this, "party-lbl", "MUILabel");
        this.totalLabel = MUIOutlet(this, "total-lbl", "MUILabel");

        this.accountDropdown = MUIOutlet(this, "account-dd", "MUIButton");
        this.accountDropdown.setAction(this, this.showSelectLedgerAccount);

        this.classificationDropdown = MUIOutlet(this, "classification-dd", "MUIButton");
        this.classificationDropdown.setAction(this, this.showSelectClassification);

        // the dropdowns keep their clicks: a click anywhere else on the head selects the invoice
        let tapGesture = new MUITapGestureRecognizer();
        tapGesture.initWithTarget(this, function(this:InvoiceSectionReconciliationHeader, gesture:MUIGestureRecognizer){
            if (gesture.state != MUIGestureRecognizerState.Ended || this._document == null) return;
            this.delegate?.headerDidSelectDocument(this, this._document);
        });
        this.addGestureRecognizer(tapGesture);
    }

    private _document:Invoice = null;
    private _lines:InvoiceLine[] = [];

    get document():Invoice { return this._document; }

    /// The invoice with the lines of its section (the ones the filters show).
    setDocument(document:Invoice, lines:InvoiceLine[]) {
        this._document = document;
        this._lines = lines ?? [];
        this.update();
    }

    set selected(value:boolean) {
        if (value) MUICoreLayerAddStyle(this.layer, "selected");
        else MUICoreLayerRemoveStyle(this.layer, "selected");
    }

    update() {
        let document = this._document;
        if (document == null) return;

        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
        let escape = ReconciliationEntryStateHelper.escape;

        this.dateLabel.text = document.date != null ? ad.dateFormatter.stringFromDate(document.date) : "";
        this.docNumberLabel.text = escape(document.documentNumber);
        this.partyLabel.text = escape(ReconciliationEntryStateHelper.legalEntityOfDocument(document)?.name);
        this.totalLabel.text = ad.currencyFormatter.stringFromNumber(document.totalAmount ?? 0);

        let venue = this.venueOfDocument(document);
        this.venueLabel.text = escape(venue);
        this.venueLabel.hidden = venue == null || venue.length == 0;

        // the default account, or what it means to have none: each line books with its own
        let account = document.ledgerAccount;
        this.accountDropdown.title = account != null ? ReconciliationEntryStateHelper.accountTitle(account) : MIOLocalizeString("NO_DEFAULT_ACCOUNT", "No default account");
        this.setInherited(this.accountDropdown, account == null);

        // the classification all the lines share; when they differ, picking one applies it to all
        let classification = this.commonClassification();
        let mixed = classification == null && this._lines.some(l => l.lineClassification != null);
        if (classification != null) this.classificationDropdown.title = escape(classification.title);
        else this.classificationDropdown.title = mixed ? MIOLocalizeString("VARIOUS", "Various") : MIOLocalizeString("CHOOSE", "Choose");
        this.setInherited(this.classificationDropdown, classification == null);

        // a locked entry is not changed from its invoice
        let locked = this.isLocked;
        this.setDropdownEnabled(this.accountDropdown, !locked);
        this.setDropdownEnabled(this.classificationDropdown, !locked);
    }

    // The business of a licence invoice, the supplier's own number of a purchase invoice
    private venueOfDocument(document:Invoice) : string {
        if (document instanceof LicenseInvoice) {
            let name = document.business?.name ?? document.businessName;
            return name != "UNKOWN" ? name : null;
        }
        if (document instanceof PurchaseInvoice) {
            let reference = document.externalReference;
            return reference != document.documentNumber ? reference : null;
        }
        return null;
    }

    /// Locked: the invoice's entry, or all its lines booked one by one
    private get isLocked() : boolean {
        return ReconciliationEntryStateHelper.stateForDocument(this._document, this._lines) == ReconciliationEntryState.locked;
    }

    private commonClassification() : DBDocumentLineClassification {
        if (this._lines.length == 0) return null;
        let first = this._lines[0].lineClassification;
        if (first == null) return null;
        return this._lines.every(l => l.lineClassification == first) ? first : null;
    }

    private setInherited(dropdown:MUIButton, inherited:boolean) {
        if (inherited) MUICoreLayerAddStyle(dropdown.layer, "rec-inherited");
        else MUICoreLayerRemoveStyle(dropdown.layer, "rec-inherited");
    }

    private setDropdownEnabled(dropdown:MUIButton, enabled:boolean) {
        dropdown.enabled = enabled;
        if (enabled) MUICoreLayerRemoveStyle(dropdown.layer, "disable");
        else MUICoreLayerAddStyle(dropdown.layer, "disable");
    }

    private showSelectLedgerAccount() {
        let document = this._document;
        if (document == null || this.isLocked) return;

        AppHelper.sharedInstance().showSelectLedgerAccountViewControllerFromView(this.accountDropdown, document.ledgerAccount, null, false, null, this, function(this:InvoiceSectionReconciliationHeader, controller:SelectEntityViewController, account:LedgerAccount){
            // another picker may answer while this one was asked for (see AppHelper)
            if (account != null && !(account instanceof LedgerAccount)) return;
            if (document.ledgerAccount == account) return;
            document.ledgerAccount = account;
            document.ledgerAccountName = account?.name ?? null;
            this.update();
            this.delegate?.headerDidChangeAccounts(this, document);
        }, MIOLocalizeString("NO_DEFAULT_ACCOUNT", "No default account"));
    }

    private showSelectClassification() {
        let document = this._document;
        if (document == null || this.isLocked) return;

        AppHelper.sharedInstance().showCustomSelectViewControllerFromView(this.classificationDropdown, "DBDocumentLineClassification", "title", null, this, function(this:InvoiceSectionReconciliationHeader, controller:SelectEntityViewController, classification:DBDocumentLineClassification){
            if (classification != null && !(classification instanceof DBDocumentLineClassification)) return;
            let changed = false;
            for (let line of this._lines) {
                if (line.lineClassification == classification) continue;
                line.lineClassification = classification;
                changed = true;
            }
            if (!changed) return;
            this.update();
            this.delegate?.headerDidChangeAccounts(this, document);
        }, true, MIOLocalizeString("NONE", "None"), this.commonClassification());
    }
}
